import { BareButton } from '@calendly/ui/components/bare-button';
import { Button } from '@calendly/ui/components/button';
import { HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, CSSProperties, ReactNode, ElementType, MouseEvent } from 'react';
import { THEMES } from './themes';
const HORIZONTAL_PLACEMENTS = ['left', 'right'] as const;
type ThemeType = keyof typeof THEMES;
type PlacementType = 'top' | 'bottom' | 'left' | 'right';
const spotlightWrapperIsPhoneCss = "sjvtkr2";
const spotlightWrapperClassicCss = "s1k5163y";
const spotlightWrapperMeetingIntelligenceCss = "sa13hpd";
const spotlightWrapperMobileMRSimplifiedCss = "s145lkpw";
const spotlightWrapperCss = "s1wc0gxd";
interface SpotlightWrapperProps {
  classic?: boolean;
  meetingIntelligence?: boolean;
  mobileMRSimplified?: boolean;
  as?: ElementType;
  top?: number | string;
  left?: number | string;
  bottom?: number | string;
  right?: number | string;
  theme: ThemeType;
  className?: string;
  children?: ReactNode;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
  'data-testid'?: string;
}
export const SpotlightWrapper = forwardRef<HTMLDivElement, SpotlightWrapperProps>((props, ref) => {
  const {
    classic,
    meetingIntelligence,
    mobileMRSimplified,
    as: AsComponent,
    top,
    left,
    bottom,
    right,
    theme,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  const style: CSSProperties = {
    top,
    left,
    bottom,
    right,
    border: `1px solid ${THEMES[theme].borderColor}`,
    backgroundColor: THEMES[theme].backgroundColor,
    color: THEMES[theme].textColor
  };
  return <Component ref={ref} {...restProps} style={style} theme={theme} className={classNames(spotlightWrapperCss, {
    [spotlightWrapperIsPhoneCss]: isPhone,
    [spotlightWrapperClassicCss]: classic,
    [spotlightWrapperMeetingIntelligenceCss]: meetingIntelligence,
    [spotlightWrapperMobileMRSimplifiedCss]: mobileMRSimplified
  }, props.className)} />;
});
const extraContentContainerCss = "eztsm3q";
interface ExtraContentContainerProps {
  as?: ElementType;
  className?: string;
  children?: ReactNode;
}
export const ExtraContentContainer = forwardRef<HTMLDivElement, ExtraContentContainerProps>((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(extraContentContainerCss, props.className)} />;
});
const extraContentImageCss = "e13tgs3u";
interface ExtraContentImageProps {
  as?: ElementType;
  src: string;
  alt: string;
  className?: string;
}
export const ExtraContentImage = forwardRef<HTMLImageElement, ExtraContentImageProps>((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  return <Component ref={ref} {...restProps} className={classNames(extraContentImageCss, props.className)} />;
});
const textContainerIsExtendedCss = "t1203xy0";
const textContainerIsPhoneCss = "tsuwlsp";
const textContainerCss = "tm2rj6s";
interface TextContainerProps {
  as?: ElementType;
  isExtended?: boolean;
  className?: string;
  children?: ReactNode;
}
export const TextContainer = forwardRef<HTMLDivElement, TextContainerProps>((props, ref) => {
  const {
    as: AsComponent,
    isExtended,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(textContainerCss, {
    [textContainerIsExtendedCss]: isExtended,
    [textContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
interface StyledHeadingTextProps {
  className?: string;
  children?: ReactNode;
}
export const StyledHeadingText = forwardRef<HTMLHeadingElement, StyledHeadingTextProps>((props, _ref) => {
  const level = '1';
  const size = 'small';
  return <HeadingText {...props} level={level} size={size} />;
});
const buttonContainerCss = "b1lm6ghl";
interface ButtonContainerProps {
  as?: ElementType;
  theme: ThemeType;
  newCardDesign?: boolean;
  className?: string;
  children?: ReactNode;
}
export const ButtonContainer = forwardRef<HTMLDivElement, ButtonContainerProps>((props, ref) => {
  const {
    as: AsComponent,
    theme,
    newCardDesign,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonContainerCss, props.className)} />;
});
const styledCloseButtonHasExtraContentCss = "s159bksz";
const styledCloseButtonCss = "sc4vtj2";
interface StyledCloseButtonProps {
  hasExtraContent?: boolean;
  theme?: ThemeType;
  className?: string;
  onClick?: () => void;
  'aria-label'?: string;
  children?: ReactNode;
}
export const StyledCloseButton = forwardRef<HTMLButtonElement, StyledCloseButtonProps>((props, ref) => {
  const {
    hasExtraContent,
    theme,
    ...restProps
  } = props;
  return <BareButton ref={ref} {...restProps} className={classNames(styledCloseButtonCss, {
    [styledCloseButtonHasExtraContentCss]: hasExtraContent
  }, props.className)} />;
});
const spacerCss = "s11u72o2";
interface SpacerProps {
  as?: ElementType;
  className?: string;
}
export const Spacer = forwardRef<HTMLDivElement, SpacerProps>((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(spacerCss, props.className)} />;
});
const arrowImageIsHorizontalCss = "a1ettoiw";
const arrowImageCss = "a1uy5cux";
interface ArrowImageProps {
  as?: ElementType;
  top?: number | string;
  bottom?: number | string;
  left?: number | string;
  right?: number | string;
  theme: ThemeType;
  placement: PlacementType;
  className?: string;
}
export const ArrowImage = forwardRef<HTMLImageElement, ArrowImageProps>((props, ref) => {
  const {
    as: AsComponent,
    top,
    bottom,
    left,
    right,
    theme,
    placement,
    ...restProps
  } = props;
  const Component = AsComponent || 'img';
  const isHorizontal = HORIZONTAL_PLACEMENTS.includes(placement as any);
  const src = THEMES[theme].arrowIconSource[placement];
  const alt = '';
  const style: CSSProperties = {
    top,
    bottom,
    left,
    right
  };
  return <Component ref={ref} {...restProps} src={src} alt={alt} style={style} className={classNames(arrowImageCss, {
    [arrowImageIsHorizontalCss]: isHorizontal
  }, props.className)} />;
});
const secondaryButtonCss = "s1wve1pt";
interface SecondaryButtonProps {
  theme: ThemeType;
  color?: string;
  size?: 'large' | 'small' | 'normal';
  decoration?: 'primary' | 'meeting-intelligence' | 'danger' | 'outline' | 'secondary-outline' | 'secondary-danger' | 'ghost';
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
}
export const SecondaryButton = forwardRef<HTMLButtonElement, SecondaryButtonProps>((props, ref) => {
  const {
    theme,
    color,
    size,
    decoration,
    ...restProps
  } = props;
  const style: CSSProperties = {
    textDecoration: THEMES[theme].textDecoration,
    width: THEMES[theme].buttonWidth,
    color
  };
  return <Button ref={ref} size={size} decoration={decoration} color={color} {...restProps} style={style} className={classNames(secondaryButtonCss, props.className)} />;
});
const primaryButtonCss = "p1o1nu5o";
interface PrimaryButtonProps {
  theme: ThemeType;
  size?: 'large' | 'small' | 'normal';
  decoration?: 'primary' | 'meeting-intelligence' | 'danger' | 'outline' | 'secondary-outline' | 'secondary-danger' | 'ghost';
  onClick?: () => void;
  className?: string;
  children?: ReactNode;
}
export const PrimaryButton = forwardRef<HTMLButtonElement, PrimaryButtonProps>((props, ref) => {
  const {
    theme,
    size,
    decoration,
    ...restProps
  } = props;
  const style: CSSProperties = {
    width: THEMES[theme].buttonWidth
  };
  return <Button ref={ref} size={size} decoration={decoration} {...restProps} style={style} className={classNames(primaryButtonCss, props.className)} />;
});
const progressContainerIsPhoneCss = "p1rvm8uc";
const progressContainerCss = "p1o4j5s7";
interface ProgressContainerProps {
  as?: ElementType;
  className?: string;
  children?: ReactNode;
}
export const ProgressContainer = forwardRef<HTMLDivElement, ProgressContainerProps>((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(progressContainerCss, {
    [progressContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});

require("./spotlight.atoms.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./spotlight.atoms.tsx");